// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    openBidDetailsState: state.xOpenBidDetails,
    showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OpenBidsDetailsInformationStyles {
    detailsGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoHeadingAndText?: SmallHeadingAndTextStyles;
    dateLabelValue?: TypographyPresentationProps;
    dateLabelText?: TypographyPresentationProps;
}

export const openBidsDetailsInformationStyles: OpenBidsDetailsInformationStyles = {};

const styles = openBidsDetailsInformationStyles;

const OpenBidsDetailsInformation = ({ openBidDetailsState, showPoNumber }: Props) => {
    const order = openBidDetailsState.dataView?.salesOrder;

    if (!order) {
        return null;
    }

    return (
        <GridContainer {...styles.detailsGridContainer}>
            <GridItem {...styles.infoGridItem}>
               <StyledWrapper>
                    <Typography {...styles.dateLabelText}>{translate("Bid Date")}:</Typography>
                    <Typography {...styles.dateLabelValue}>
                        <LocalizedDateTime
                            dateTime={order.orderHeader.orderDate.date}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
               </StyledWrapper>
            </GridItem>
            {showPoNumber && order.orderHeader.customerPo && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("PO #")}
                        text={order.orderHeader.customerPo}
                        extendedStyles={styles.infoHeadingAndText}
                    />
                </GridItem>
            )}
            {order.orderHeader.shipDate.date && (
                <GridItem {...styles.infoGridItem}>
                    <Typography {...styles.dateLabelText}>{translate("Ship Date")}:</Typography>
                    <Typography {...styles.dateLabelValue}>
                        <LocalizedDateTime
                            dateTime={order.orderHeader.shipDate.date}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </GridItem>
            )}
            {order.orderHeader.outsideSalesPerson.eclipseId && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Salesperson")}
                        text={order.orderHeader.outsideSalesPerson.eclipseId}
                        extendedStyles={styles.infoHeadingAndText}
                    />
                </GridItem>
            )}
            {order.orderHeader.billTo.entity.entityId && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Customer Number")}
                        text={order.orderHeader.billTo.entity.entityId}
                        extendedStyles={styles.infoHeadingAndText}
                    />
                </GridItem>
            )}
            {order.orderHeader.orderStatus.text && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Status")}
                        text={order.orderHeader.orderStatus.text}
                        extendedStyles={styles.infoHeadingAndText}
                    />
                </GridItem>
            )}
            {order.orderHeader.customerReleaseNumber && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Release #")}
                        text={order.orderHeader.customerReleaseNumber}
                        extendedStyles={styles.infoHeadingAndText}
                        data-test-selector="Release #"
                    />
                </GridItem>
            )}
            {order.orderHeader.writtenBy.eclipseId && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Writer")}
                        text={order.orderHeader.writtenBy.eclipseId}
                        extendedStyles={styles.infoHeadingAndText}
                        data-test-selector="Writer"
                    />
                </GridItem>
            )}
            {order.orderHeader.paymentTerms.description && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Terms")}
                        text={order.orderHeader.paymentTerms.description}
                        extendedStyles={styles.infoHeadingAndText}
                        data-test-selector="Terms"
                    />
                </GridItem>
            )}
            {order.orderHeader.shippingInformation.shipVia.description && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Ship Via")}
                        text={order.orderHeader.shippingInformation.shipVia.shipViaId}
                        extendedStyles={styles.infoHeadingAndText}
                        data-test-selector="Ship Via"
                    />
                </GridItem>
            )}
            {order.orderHeader.orderedBy.description && (
                <GridItem {...styles.infoGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Ordered By")}
                        text={order.orderHeader.orderedBy.description}
                        extendedStyles={styles.infoHeadingAndText}
                        data-test-selector="Ordered By"
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(OpenBidsDetailsInformation),
    definition: {
        displayName: "Open Bids Details Information",
        allowedContexts: ["OpenBidsDetailsPage"],
        group: XNGAGE_ELEMENTS,
    },
};

export default widgetModule;
